import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../shared/services/auth.service';
import { Category } from '../../documents/models/category';
import { Document } from '../../documents/models/document';
import { AOAISettings, Message, RawResponseMessage, RequestPayload } from '../models/chat';

const BACKEND_URL = environment.apiUrl + '/chats';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private http = inject(HttpClient);
  private injector = inject(Injector);

  private _sessionId = signal<string | null>(null);
  get sessionId() {
    return this._sessionId.asReadonly();
  }

  private _messages = signal<Message[]>([]);
  get messages() {
    return this._messages.asReadonly();
  }

  private _status = signal<'idle' | 'loading' | 'success' | 'error'>('idle');
  get status() {
    return this._status.asReadonly();
  }

  private abortController: AbortController | null = null;
  get isStreaming() {
    return this._status() === 'loading' && this.abortController !== null;
  }

  private _contextCategory = signal<Category | null>(null);
  get contextCategory() {
    return this._contextCategory.asReadonly();
  }

  private _contextDocuments = signal<Document[]>([]);
  get contextDocuments() {
    return this._contextDocuments.asReadonly();
  }

  AOAISettings = signal<AOAISettings>({
    maxTokens: 800,
    temperature: 0.1,
    deploymentName: environment.deploymentNames[0] || 'doailabs-gpt-4.1',
    deploymentApiVersion: environment.deploymentApiVersions[0] || '2024-12-01-preview',
  });

  private getContext() {
    const category = this.contextCategory();
    const documents = this.contextDocuments();
    const context: { category?: string; document_id?: string[]; } = {};

    if (category) {
      context.category = category.name;
    }
    if (documents.length > 0) {
      context.document_id = documents.map(doc => doc.id);
    }

    return context;
  }

  chat(requestPayload: RequestPayload) {
    if (this._status() === 'loading') {
      return;
    }
    this._status.set('loading');
    const sessionId = this.sessionId();

    this._messages.update(msgs => [
      ...msgs,
      {
        role: 'user',
        createdAt: new Date(),
        requestId: '',
        interactionId: '',
        reply: requestPayload.userInput,
        sources: [],
      } as Message,
    ]);

    const AOAISettings = this.AOAISettings();

    this.http
      .post<RawResponseMessage>(
        `${BACKEND_URL}/chat`,
        {
          user_input: requestPayload.userInput,
          session_id: sessionId || undefined,
          max_tokens: AOAISettings.maxTokens,
          temperature: AOAISettings.temperature,
          deployment_name: AOAISettings.deploymentName,
          deployment_api_version: AOAISettings.deploymentApiVersion,
          ...this.getContext()
        },
        {
          withCredentials: true,
        }
      )
      .subscribe({
        next: response => {
          this._sessionId.set(response.session_id);
          this._messages.update(msgs => [
            ...msgs,
            {
              role: 'assistant',
              createdAt: new Date(response.created_at),
              requestId: response.request_id,
              reply: response.reply,
              sources: response.sources.map(src => ({
                sourceFile: src.source_file,
                sourcePage: src.source_page,
                docId: src.doc_id,
                documentName: src.document_name,
              })),
              status: response.success ? 'success' : 'error',
            } as Message,
          ]);

          this._status.set('success');
        },
        error: error => {
          this._status.set('error');
        },
      });
  }

  stream(requestPayload: RequestPayload) {
    if (this._status() === 'loading') return;

    this._status.set('loading');
    const authService = this.injector.get(AuthService);
    const authToken = authService.accessToken();
    const sessionId = this.sessionId();
    const tenantUuid = authService.userConfig()?.company?.uuid || '';

    this._messages.update(msgs => [
      ...msgs,
      {
        role: 'user',
        createdAt: new Date(),
        requestId: '',
        interactionId: '',
        reply: requestPayload.userInput,
        sources: [],
      } as Message,
    ]);

    const observable = new Observable(observer => {
      this.abortController = new AbortController();

      const AOAISettings = this.AOAISettings();

      fetch(`${BACKEND_URL}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken ? `Bearer ${authToken}` : '',
          'X-Tenant-Uuid': tenantUuid,
        },
        body: JSON.stringify({
          user_input: requestPayload.userInput,
          session_id: sessionId || undefined,
          max_tokens: AOAISettings.maxTokens,
          temperature: AOAISettings.temperature,
          deployment_name: AOAISettings.deploymentName,
          deployment_api_version: AOAISettings.deploymentApiVersion,
          ...this.getContext()
        }),
        signal: this.abortController.signal,
        credentials: 'include',
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.body?.getReader();
        })
        .then(reader => {
          if (!reader) {
            throw new Error('Failed to get reader from response body');
          }

          const decoder = new TextDecoder();

          const readStream = () => {
            reader
              .read()
              .then(({ done, value }) => {
                try {
                  const rawData = decoder.decode(value, { stream: false });
                  const lines = rawData.split('\n').filter(line => line.trim() !== '');

                  for (const line of lines) {
                    const data = line?.replace(/^data: /, '').trim();
                    try {
                      if (data) {
                        const jsonData = JSON.parse(data);
                        observer.next(jsonData);
                      }
                    } catch (jsonError) {
                      console.error('Error parsing JSON:', jsonError);
                    }
                  }
                } catch (error) {
                  console.error('Error decoding data:', error);
                }

                if (done) {
                  observer.complete();
                  return;
                }
                readStream();
              })
              .catch(error => {
                if (error.name !== 'AbortError') {
                  console.error('Error reading stream:', error);
                  observer.error(error);
                }
              });
          };

          readStream();
        })
        .catch(error => {
          this.abortController?.abort();
          if (error.name !== 'AbortError') {
            console.error('Error in fetch:', error);
            observer.error(error);
          }
        });

      return () => {
        this.abortController?.abort();
      };
    });

    const newMessage: Message = {
      role: 'assistant',
      createdAt: new Date(),
      requestId: '',
      interactionId: '',
      reply: 'Loading...',
      sources: [],
      status: 'loading',
      completed: false,
    };

    let chunkMessage = '';
    let rawMsg = '';

    const updateMessage = (message: Message) => {
      this._messages.update(msgs => {
        const index = msgs.findIndex(msg => msg.createdAt.getTime() === message.createdAt.getTime() && msg.role === 'assistant');
        if (index !== -1) {
          msgs[index] = { ...message };
        } else {
          msgs.push(message);
        }
        return [...msgs];
      });
    };

    updateMessage(newMessage);

    const extractAndRemoveSources = (content: string): { cleanContent: string; sources: Array<{ sourceFile: string; sourcePage: number | string; docId: string; documentName: string; }>; } => {
      const sources: Array<{ sourceFile: string; sourcePage: number | string; docId: string; documentName: string; }> = [];

      const singlePattern = /\[source_file:\s*([^\|\]]+)\s*\|\s*source_page:\s*([^\|\]]+)\s*\|\s*doc_id:\s*([^\]]+)\]/g;
      let match;

      while ((match = singlePattern.exec(content)) !== null) {
        const sourceFile = match[1].trim();
        const sourcePage = match[2].trim();
        const docId = match[3].trim();
        sources.push({
          sourceFile: sourceFile,
          sourcePage: /^\d+$/.test(sourcePage) ? parseInt(sourcePage) : sourcePage,
          docId: docId,
          documentName: sourceFile,
        });
      }

      const multiPattern = /\[source_file:\s*([^\|\]]+)\s*\|\s*source_pages:\s*([^\|\]]+)\s*\|\s*doc_id:\s*([^\]]+)\]/g;

      while ((match = multiPattern.exec(content)) !== null) {
        const sourceFile = match[1].trim();
        const sourcePages = match[2].trim();
        const docId = match[3].trim();

        const pageList = sourcePages.split(',').map(page => page.trim());
        for (const page of pageList) {
          sources.push({
            sourceFile: sourceFile,
            sourcePage: /^\d+$/.test(page) ? parseInt(page) : page,
            docId: docId,
            documentName: sourceFile,
          });
        }
      }

      const cleanContent = content.replace(/\s*\[source_file:\s*[^\]]+\s*\|\s*source_pages?:\s*[^\]]+\s*\|\s*doc_id:\s*[^\]]+\]/g, '').trim();

      return { cleanContent, sources };
    };

    observable.subscribe({
      next: (data: any) => {
        switch (data?.type) {
          case 'session_id':
            this._sessionId.set(data.session_id);
            break;
          case 'status':
            newMessage.reply = data.message;
            break;
          case 'content':
            chunkMessage += data.delta;
            rawMsg += data.delta;
            const { cleanContent: reply, sources: src } = extractAndRemoveSources(chunkMessage);
            newMessage.reply = reply;
            // newMessage.sources = src.map(s => ({
            //   sourcePage: s.sourcePage,
            //   docId: s.docId,
            //   documentName: s.documentName,
            // })) as Message['sources'];
            newMessage.status = 'generating';
            break;
          case 'error':
            this._status.set('error');
            newMessage.status = 'error';
            newMessage.reply = data.message || 'An error occurred while generating the answer';
            newMessage.completed = true;
            newMessage.requestId = data.request_id;
            newMessage.interactionId = data.interaction_id;
            break;
          case 'final':
            const { cleanContent: reply2, sources: src2 } = extractAndRemoveSources(rawMsg);
            newMessage.requestId = data.request_id;
            newMessage.interactionId = data.interaction_id;
            newMessage.reply = reply2;
            newMessage.sources = data.sources?.map((s: any) => ({
              sourceFile: s.source_file,
              sourcePage: s.source_page,
              docId: s.doc_id,
              documentName: s.document_name,
            })) as Message['sources'] || [];
            newMessage.rawMsg = rawMsg;
            break;
          case 'done':
            newMessage.status = 'success';
            newMessage.completed = true;
            break;
          default:
            console.warn('Unknown message type:', data);
        }
        updateMessage(newMessage);
      },
      error: error => {
        this._status.set('error');
        newMessage.status = 'error';
        newMessage.reply = 'An error occurred while generating the answer';
        console.error('Stream error:', error);
      },
      complete: () => {
        if (this.status() !== 'error') {
          this._status.set('success');
        }
        newMessage.completed = true;
        updateMessage(newMessage);
      },
    });
  }

  abortStream() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this._status.set('idle');
    }
  }

  sendFeedback(interactionId: string, feedback: string, comments?: string): Observable<any> {
    return this.http.post(
      `${BACKEND_URL}/feedback`,
      {
        session_id: this.sessionId(),
        interaction_id: interactionId,
        feedback: feedback,
        comment: comments,
      },
      {
        withCredentials: true,
      }
    );
  }

  setContextCategory(category: Category | null) {
    this._contextCategory.update(() => category);
  }

  addDocumentToContext(document: Document) {
    if (!this._contextDocuments().includes(document)) {
      this._contextDocuments.update(docs => [...docs, document]);
    }
  }

  removeDocumentFromContext(document: string | Document) {
    const documentId = typeof document === 'string' ? document : document.id;
    this._contextDocuments.update(docs => docs.filter(doc => doc.id !== documentId));
  }

  clearContext() {
    this._contextCategory.set(null);
    this._contextDocuments.set([]);
  }

  clearChat() {
    this._sessionId.set(null);
    this._messages.set([]);
    this._status.set('idle');
  }
}
